// GoogleAd.jsx
import { useEffect, useRef } from "react";

// adsbygoogle.push() THROWS synchronously once the library is loaded (e.g.
// "Fluid responsive ads must be at least 250px wide" for a hidden or narrow
// slot). Inside useEffect an uncaught throw unmounts the whole React root,
// so every push is guarded: a failed ad slot must never blank the page.
function pushSlot(el) {
  if (!el || el.getAttribute("data-adsbygoogle-status")) return;
  try {
    (window.adsbygoogle = window.adsbygoogle || []).push({});
  } catch (e) {
    // Ad slot skipped; the page keeps rendering.
  }
}
// GT-1 (2026-11-03): AdSense waits for the OBTO consent loader
// (consent.js?t=hazel in the page). The loader sets this flag only after the
// visitor clicks "Allow measurement" (never with DNT/GPC or "No thanks").
// B23 (2026-11-05): outside the EEA, UK and CH the Bloom consent gate sets
// __bloomAdsReady at once, with requestNonPersonalizedAds = 1 after an opt-out.
function consentGranted() {
  return window.__obtoMarketingMeasurementReady === true || window.__bloomAdsReady === true;
}


export default function GoogleAd({
  client = "ca-pub-9830349886694199",
  slot = "2074356695",
  layoutKey = "-d8+a2-1x-fp+zc",
  format = "fluid",
  style = { display: "block" },
}) {
  const adRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const scriptSrc =
      `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`;

    const load = () => {
      // Inject the AdSense script once
      if (!document.querySelector(`script[src^="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]`)) {
        const s = document.createElement("script");
        s.async = true;
        s.src = scriptSrc;
        s.setAttribute("crossorigin", "anonymous");
        document.head.appendChild(s);
        s.addEventListener("load", () => pushSlot(adRef.current));
      } else {
        // Script already present — just push this slot
        pushSlot(adRef.current);
      }
    };

    if (consentGranted()) { load(); return; }
    // Wait for "Allow measurement"; nothing goes to Google before it.
    const timer = setInterval(() => {
      if (consentGranted()) { clearInterval(timer); load(); }
    }, 500);
    return () => clearInterval(timer);
  }, [client, slot, layoutKey, format]);

  return (
    <ins
      ref={adRef}
      className="adsbygoogle"
      style={style}
      data-ad-client={client}
      data-ad-slot={slot}
      data-ad-format={format}
      data-ad-layout-key={layoutKey}
    />
  );
}
